import React from 'react'
import { useRouter } from 'next/navigation'

import { useTenant } from '@rewardops/auth'
import { useLocale } from '@rewardops/ui'
import { PromotionBannerProps } from '@rewardops/utils'

import { useFilterBySegmentationTags } from '../hooks/use-filter-by-segmentation-tags'
import ResponsiveComponent from '../responsive-component'

type Props = {
  content: PromotionBannerProps
}

const RetailerPromotionBanner: React.FC<Props> = ({ content }: Props) => {
  const {
    isDark,
    backgroundColor,
    backgroundImage,
    backgroundImageMobile,
    title,
    description,
    link,
    brandLogo,
  } = content

  const { push } = useRouter()
  const tenant = useTenant()
  const { locale } = useLocale()
  const isBannerSupportedForSegment = useFilterBySegmentationTags({
    bannerContent: content,
  })

  const handlePromoClick = () => {
    if (link?.isRelativeUrl !== false) {
      return push(link?.to ?? '/')
    }

    const externalLink = link?.to?.startsWith('/')
      ? `${tenant?.publicUrl}/${locale}/${link?.to}`
      : link?.to

    window.open(externalLink, '_blank')
  }

  const retailerLogo = (
    <div className="flex items-center">
      <div className="h-[70px] w-[130px]">
        <img
          className="h-full w-full rounded-lg object-cover"
          src={brandLogo?.file?.url}
          alt={`${brandLogo?.title ?? 'retailer'}-logo`}
        />
      </div>
    </div>
  )

  const bannerContent = (
    <div
      className={`flex flex-col justify-center gap-6 ${
        isDark ? 'text-white' : 'text-black'
      }`}
    >
      <div
        className={`-mb-3 ${
          tenant?.fonts?.secondary
            ? 'text-base font-semibold'
            : 'text-xs font-medium'
        }`}
        style={
          tenant?.fonts?.secondary
            ? { fontFamily: 'var(--font-secondary)' }
            : undefined
        }
      >
        {title?.toUpperCase()}
      </div>

      <div className={`text-3xl font-normal`}>{description}</div>

      <div
        className="cursor-pointer text-lg underline"
        onClick={handlePromoClick}
      >
        {link?.name}
      </div>
    </div>
  )

  const desktopComponent = (
    <div className="col-span-2 mx-auto mb-[100px] h-auto w-full max-w-[1140px] overflow-hidden">
      <div className="flex h-[254px] w-full">
        <div className="flex-1">
          <img
            src={backgroundImage?.file?.url}
            className="h-full w-full rounded-l-3xl object-cover"
            alt={`${backgroundImage?.title}-retailer-banner`}
          />
        </div>
        <div
          className="flex-[1.5] rounded-r-3xl px-12 py-6"
          style={{ background: backgroundColor }}
        >
          <div className="flex h-full items-center gap-12">
            {retailerLogo}
            {bannerContent}
          </div>
        </div>
      </div>
    </div>
  )

  const mobileComponent = (
    <div className="col-span-2 mx-auto mb-[60px] w-full max-w-full overflow-hidden">
      <div className="flex h-auto flex-col">
        <div className="h-[300px] w-full">
          <img
            src={
              backgroundImageMobile
                ? backgroundImageMobile?.file?.url
                : backgroundImage?.file?.url
            }
            className="h-full w-full object-cover"
            alt={`${backgroundImage?.title}-retailer-banner`}
          />
        </div>
        <div className="w-full p-6" style={{ background: backgroundColor }}>
          <div className="flex flex-col gap-6">
            {retailerLogo}
            {bannerContent}
          </div>
        </div>
      </div>
    </div>
  )

  if (!Boolean(isBannerSupportedForSegment)) {
    return null
  }

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}

export { RetailerPromotionBanner }
